package ai.wagemix.pages.dev

import ai.wagemix.net.models.WMBadge
import ai.wagemix.web.components.WMAvatar
import ai.wagemix.web.components.WMBadge
import ai.wagemix.web.components.WMBadgeKind
import ai.wagemix.web.components.WMBandChart
import ai.wagemix.web.components.WMBandChartSize
import ai.wagemix.web.components.WMBandChartModel
import ai.wagemix.web.components.WMBandChartPoint
import ai.wagemix.web.components.WMButton
import ai.wagemix.web.components.WMButtonKind
import ai.wagemix.web.components.WMButtonSize
import ai.wagemix.web.components.WMCard
import ai.wagemix.web.components.WMChartCard
import ai.wagemix.web.components.WMChartSource
import ai.wagemix.web.components.WMCheckbox
import ai.wagemix.web.components.WMDialog
import ai.wagemix.web.components.WMDialogButtonStyle
import ai.wagemix.web.components.WMEmptyState
import ai.wagemix.web.components.WMFeatureCard
import ai.wagemix.web.components.WMField
import ai.wagemix.web.components.WMIcon
import ai.wagemix.web.components.WMIconButton
import ai.wagemix.web.components.WMIconGlyph
import ai.wagemix.web.components.WMMeter
import ai.wagemix.web.components.WMMixCard
import ai.wagemix.web.components.WMMixCardOccupation
import ai.wagemix.web.components.WMMixCardPresentation
import ai.wagemix.web.components.WMMixCardStrings
import ai.wagemix.web.components.WMNotice
import ai.wagemix.web.components.WMNoticeKind
import ai.wagemix.web.components.WMOccupationRow
import ai.wagemix.web.components.WMOccupationRowPresentation
import ai.wagemix.web.components.WMOccupationRowStrings
import ai.wagemix.web.components.WMPlan
import ai.wagemix.web.components.WMPlanCard
import ai.wagemix.web.components.WMPlanFeature
import ai.wagemix.web.components.WMSegmentedControl
import ai.wagemix.web.components.WMSelect
import ai.wagemix.web.components.WMSelectOption
import ai.wagemix.web.components.WMSlider
import ai.wagemix.web.components.WMStatTile
import ai.wagemix.web.components.WMStatTileSize
import ai.wagemix.web.components.WMStepper
import ai.wagemix.web.components.WMStory
import ai.wagemix.web.components.WMStoryCard
import ai.wagemix.web.components.WMTextField
import ai.wagemix.web.components.WMTierBadge
import ai.wagemix.web.components.WMToggle
import ai.wagemix.web.components.of
import ai.wagemix.web.theme.WMCardGridGapVar
import ai.wagemix.web.theme.WMPageTitleTextStyle
import ai.wagemix.web.theme.WMSectionContentGapVar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.fsryan.foundation.web.components.FSContainer
import com.fsryan.foundation.web.components.FSResponsiveGrid
import com.fsryan.foundation.web.theme.FSMarginVar
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.px

/*
 * The component gallery (plan task DS-9): every Wagemix component with sample data, for a
 * side-by-side comparison with the Penpot "Components" page in both colour modes. It is a
 * development aid only and is excluded from the static export (see kobweb.app.export).
 */
@Page("/dev/components")
@Composable
fun ComponentsGalleryPage() {
    var colorMode by ColorMode.currentState
    FSContainer {
        Column(modifier = Modifier.fillMaxWidth().gap(WMSectionContentGapVar.value()).padding(topBottom = 32.px)) {
            Row(modifier = Modifier.fillMaxWidth().gap(FSMarginVar.value()), verticalAlignment = Alignment.CenterVertically) {
                SpanText("Wagemix components", WMPageTitleTextStyle.toModifier().fillMaxWidth())
                WMIconButton(
                    glyph = if (colorMode.isDark) WMIconGlyph.Sun else WMIconGlyph.Moon,
                    accessibilityString = "Toggle colour mode",
                    kind = WMButtonKind.Secondary,
                    onClick = { colorMode = colorMode.opposite }
                )
            }

            Section("Buttons") {
                Row(modifier = Modifier.gap(12.px), verticalAlignment = Alignment.CenterVertically) {
                    WMButton("Save mix", leadingIcon = WMIconGlyph.Bookmark) {}
                    WMButton("Share", kind = WMButtonKind.Secondary, leadingIcon = WMIconGlyph.Share2) {}
                    WMButton("Clear", kind = WMButtonKind.Ghost) {}
                    WMButton("Delete mix", kind = WMButtonKind.Danger) {}
                    WMButton("Disabled", enabled = false) {}
                }
                Row(modifier = Modifier.gap(12.px), verticalAlignment = Alignment.CenterVertically) {
                    WMButton("Upgrade to Pro", size = WMButtonSize.Sm, trailingIcon = WMIconGlyph.ArrowRight) {}
                    WMButton("See plans", kind = WMButtonKind.Secondary, size = WMButtonSize.Sm) {}
                    WMIconButton(WMIconGlyph.EllipsisVertical, "More") {}
                    WMIconButton(WMIconGlyph.Pencil, "Rename", kind = WMButtonKind.Secondary, size = WMButtonSize.Sm) {}
                }
            }

            Section("Badges, avatar, icons") {
                Row(modifier = Modifier.gap(8.px), verticalAlignment = Alignment.CenterVertically) {
                    WMBadge("Neutral")
                    WMBadge("In mix", kind = WMBadgeKind.Primary)
                    WMBadge("From job description", kind = WMBadgeKind.AI, icon = WMIconGlyph.Sparkles)
                    WMBadge("Premium", kind = WMBadgeKind.Warning, icon = WMIconGlyph.Lock)
                    WMBadge("Failed", kind = WMBadgeKind.Error)
                    WMBadge("Mix ready", kind = WMBadgeKind.Success, icon = WMIconGlyph.CircleCheck)
                    WMBadge("BASIC", kind = WMBadgeKind.Plan)
                    WMAvatar("RS", accessibilityString = "Riley Santos")
                }
                Row(modifier = Modifier.gap(12.px).fillMaxWidth().padding(top = 8.px).let { it }) {
                    WMIconGlyph.entries.take(25).forEach { WMIcon(it, accessibilityString = it.name) }
                }
                Row(modifier = Modifier.gap(12.px).fillMaxWidth()) {
                    WMIconGlyph.entries.drop(25).forEach { WMIcon(it, accessibilityString = it.name) }
                }
            }

            Section("Inputs and controls") {
                var text by remember { mutableStateOf("") }
                var segment by remember { mutableStateOf(0) }
                var checked by remember { mutableStateOf(true) }
                var toggled by remember { mutableStateOf(false) }
                var slider by remember { mutableStateOf(60.0) }
                var years by remember { mutableStateOf(2) }
                FSResponsiveGrid(compact = 1, expanded = 2, gap = WMCardGridGapVar.value()) {
                    WMField("Mix title", helperText = "Shown on your saved mixes and share links.", controlId = "gallery-title") {
                        WMTextField(text, onTextChange = { text = it }, placeholder = "e.g. Software Engineering Career Path")
                    }
                    WMField("Search", errorText = if (text.length == 1) "Type at least two characters" else null) {
                        WMTextField(text, onTextChange = { text = it }, placeholder = "Add an occupation…", leadingIcon = WMIconGlyph.Search)
                    }
                    WMField("Area type") {
                        WMSelect(
                            options = listOf("none", "us", "state", "msa").map { WMSelectOption(it, it.uppercase()) },
                            selectedValue = "state",
                            onSelect = {}
                        )
                    }
                    Column(modifier = Modifier.gap(12.px)) {
                        WMSegmentedControl(listOf("Annual", "Hourly"), segment, { segment = it }, accessibilityString = "Wage basis")
                        Row(modifier = Modifier.gap(16.px), verticalAlignment = Alignment.CenterVertically) {
                            WMStepper("$years yrs", { years-- }, { years++ }, "Fewer projected years", "More projected years", canDecrement = years > 0, canIncrement = years < 10)
                            WMCheckbox(checked, { checked = it }, label = "Sharing on a job description")
                            WMToggle(toggled, { toggled = it }, accessibilityString = "Include in mix")
                        }
                        WMSlider(slider, { slider = it }, accessibilityString = "Weight")
                        WMMeter(2.0 / 3.0, accessibilityString = "2 of 3 uploads used")
                    }
                }
            }

            Section("Stat tiles, chart card and notices") {
                FSResponsiveGrid(compact = 2, expanded = 4, gap = WMCardGridGapVar.value()) {
                    WMStatTile("Median", "$51,800", caption = "2025 · per year · +3.2% vs 2024", highlight = true)
                    WMStatTile("Middle 50%", "$37K – $81K", caption = "P25 – P75 · 2025")
                    WMStatTile("Typical range", "$31K – $129K", caption = "P10 – P90 · 2025")
                    WMStatTile("2027 median", "$53,500", caption = "Projected · +3.3% from 2025", projected = true)
                }
                FSResponsiveGrid(compact = 2, expanded = 4, gap = WMCardGridGapVar.value()) {
                    WMStatTile("Median", "$51,800", caption = "2025", size = WMStatTileSize.Compact, highlight = true)
                    WMStatTile("Middle 50%", "$37K – $81K", caption = "P25 – P75", size = WMStatTileSize.Compact)
                }
                WMChartCard(
                    title = "Pay range over time",
                    subtitle = "Annual wages · California · All industries",
                    controls = { WMSegmentedControl(listOf("Annual", "Hourly"), 0, {}) },
                    footer = { WMChartSource("Source: BLS OEWS") }
                ) {
                    WMBandChart(sampleChart(), description = "Weighted pay range 2021–2027, projected from 2026", size = WMBandChartSize.fixedHeight, eager = true)
                }
                WMNotice("Some values reach the BLS reporting cap", kind = WMNoticeKind.Warning, message = "P75 and P90 for 2 occupations are minimum bounds. Actual wages may be higher.")
                WMNotice("2026–2027 are projected", kind = WMNoticeKind.AI, message = "Projected with linear regression from 2021–2025 data. Points mark data years.")
                WMNotice("Share link could not be created", kind = WMNoticeKind.Error, message = "Try again in a moment.")
                WMNotice("Shown on your saved mixes and share links.", kind = WMNoticeKind.Info)
            }

            Section("Rows and cards") {
                val strings = WMOccupationRowStrings("Include in mix", "Remove", "Weight of", "Lock weight", "Unlock weight", "Upgrade to adjust weights")
                WMCard(title = "Occupations", subtitle = "100% total") {
                    WMOccupationRow(WMOccupationRowPresentation.of(1, "Education Administrators, Preschool", "SOC 11-9031 · Management", 60.0), strings, onToggleLock = {}, onToggleEnabled = {}, onRemove = {})
                    WMOccupationRow(WMOccupationRowPresentation.of(2, "Graphic Designers", "SOC 27-1024 · Arts and Design", 25.0, locked = true), strings, onToggleLock = {}, onToggleEnabled = {}, onRemove = {})
                    WMOccupationRow(WMOccupationRowPresentation.of(3, "Childcare Workers", "SOC 39-9011 · Personal Care and Service", 15.0), strings, weightsLocked = true, onRemove = {})
                }
                FSResponsiveGrid(compact = 1, expanded = 3, gap = WMCardGridGapVar.value()) {
                    WMMixCard(
                        WMMixCardPresentation.of(
                            mixId = "1", title = "Preschool Program Director", meta = "3 occupations · Updated Oct 18", fromJobDescription = true,
                            occupations = listOf(WMMixCardOccupation.of("60%", "Education Administrators, Preschool"), WMMixCardOccupation.of("25%", "Graphic Designers"), WMMixCardOccupation.of("15%", "Childcare Workers")),
                            medianText = "$51.8K", medianCaption = "Median · 2025", sparkline = sampleChart()
                        ),
                        WMMixCardStrings("From job description", "…", "Median trend of")
                    ) {
                        WMButton("Open in builder", size = WMButtonSize.Sm) {}
                        WMIconButton(WMIconGlyph.Share2, "Share", kind = WMButtonKind.Secondary, size = WMButtonSize.Sm) {}
                        WMIconButton(WMIconGlyph.EllipsisVertical, "More", size = WMButtonSize.Sm) {}
                    }
                    WMFeatureCard(WMIconGlyph.Sparkles, "AI mix from a job description", "Upload a PDF or paste a posting URL from Lever, Workday, iCIMS and more. AI maps it to weighted occupations.")
                    WMStoryCard(WMStory("The offer was for a role that's half administrator, half designer. Wagemix put it below the BLS median for that mix, so I countered with numbers instead of a hunch.", listOf("Education Administrator", "Graphic Designer"), "SM", "Sarah M.", "Preschool Director"))
                }
                FSResponsiveGrid(compact = 1, expanded = 3, gap = WMCardGridGapVar.value()) {
                    WMPlanCard(WMPlan("Free", "$0", "Free forever", "For exploring what a mixed role pays.", listOf(WMPlanFeature("Any occupation mix, equal weights"), WMPlanFeature("Annual and hourly band chart"), WMPlanFeature("Adjust occupation weights", included = false)))) {
                        WMButton("Get started", kind = WMButtonKind.Secondary, fullWidth = true) {}
                    }
                    WMPlanCard(WMPlan("Basic", "$—", "Billed yearly", "For candidates checking an offer or making a pay case.", listOf(WMPlanFeature("Everything in Free"), WMPlanFeature("Adjustable weights with sliders"), WMPlanFeature("3 AI job-description mixes")), highlighted = true, highlightLabel = "Most popular")) {
                        WMButton("Choose Basic", fullWidth = true) {}
                    }
                    WMEmptyState(WMIconGlyph.Bookmark, "No saved mixes yet", "Build a mix and save it to compare it later or share it.") {
                        WMButton("New mix", leadingIcon = WMIconGlyph.Plus) {}
                    }
                }
                WMTierBadge(WMBadge.Gold, "Gold tier", "$52K – $60K · Range midpoint above the median")
            }

            Section("Dialog") {
                var open by remember { mutableStateOf(false) }
                WMButton("Open save-mix dialog", kind = WMButtonKind.Secondary) { open = true }
                if (open) {
                    WMDialog(
                        title = "Save mix",
                        description = "Save this mix to compare it later or share it.",
                        onDismiss = { open = false },
                        actions = {
                            WMButton("Cancel", kind = WMButtonKind.Ghost, modifier = WMDialogButtonStyle.toModifier()) { open = false }
                            WMButton("Save mix", modifier = WMDialogButtonStyle.toModifier()) { open = false }
                        }
                    ) {
                        var title by remember { mutableStateOf("Preschool Program Director") }
                        WMField("Mix title", helperText = "Shown on your saved mixes and share links.") {
                            WMTextField(title, onTextChange = { title = it })
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun Section(title: String, content: @Composable () -> Unit) {
    Column(modifier = Modifier.fillMaxWidth().gap(16.px)) {
        SpanText(title, ai.wagemix.web.theme.WMCardTitleTextStyle.toModifier())
        content()
    }
}

private fun sampleChart(): WMBandChartModel = WMBandChartModel.of(
    points = listOf(
        WMBandChartPoint.of(2021, 26_000.0, 33_000.0, 44_000.0, 70_000.0, 112_000.0),
        WMBandChartPoint.of(2022, 27_000.0, 34_500.0, 46_000.0, 73_000.0, 116_000.0),
        WMBandChartPoint.of(2023, 28_000.0, 35_500.0, 47_500.0, 76_000.0, 120_000.0),
        WMBandChartPoint.of(2024, 29_000.0, 37_000.0, 49_000.0, 79_000.0, 126_000.0),
        WMBandChartPoint.of(2025, 31_000.0, 37_500.0, 51_800.0, 81_000.0, 129_000.0),
        WMBandChartPoint.of(2026, 32_000.0, 38_600.0, 52_700.0, 83_500.0, 133_000.0),
        WMBandChartPoint.of(2027, 33_000.0, 39_800.0, 53_500.0, 86_000.0, 137_000.0)
    ),
    lastActualYear = 2025,
    hourly = false
)
